if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');

        this.initializeAddonListeners();

        this.quantityInput.addEventListener('change', () => {
          this.updateSum();
        });

        this.updateQuantityInput();

        // For combined listings, build a product mapping from all available inputs
        this.productCombinationMap = null;
      }

      updatePrice(sumEl) {
        const priceEl = this.querySelector(`#price-${this.dataset.section}`);
        if (!priceEl) return;

        // Update the visible price item in place to keep the price snippet's markup and typography
        const priceItem =
          priceEl.querySelector('.price--on-sale .price-item--sale') || priceEl.querySelector('.price-item--regular');
        if (priceItem) {
          priceItem.innerHTML = sumEl.innerHTML;
        } else {
          priceEl.innerHTML = sumEl.innerHTML;
        }
      }

      updateSum() {
        const selector = `#Sum-${this.dataset.section}`;

        // add the addons
        const addons = this.querySelectorAll('[js-addon-option]:checked');
        const sumEl = this.querySelector(selector);

        if (!sumEl) return;

        const formatMoney = (amount) => `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;

        // "Shop Individual Items" (nb-individual-items.liquid): once any piece has a quantity,
        // the cart gets just those pieces instead of the set (see product-form.js).
        const individualItems = [...this.querySelectorAll('[js-individual-item]')].filter(
          (input) => parseInt(input.value) > 0
        );
        if (individualItems.length > 0) {
          const individualTotal = individualItems.reduce(
            (sum, input) => sum + parseFloat(input.dataset.price.replace(/,/g, '')) * parseInt(input.value),
            0
          );
          sumEl.innerHTML = formatMoney(Math.round(individualTotal * 100) / 100);
          return;
        }

        let price = parseFloat(sumEl.dataset.price.replace(/,/g, ''));

        let addOnTotal = 0;

        addons.forEach((addon) => {
          // Addons a Discount Kit rule makes free (see nb-addons.liquid) are
          // discounted to $0 at checkout, so they don't count toward the displayed total.
          if (addon.dataset.addonFree === 'true') return;
          addOnTotal += parseFloat(addon.dataset.price.replace(/,/g, '')) || 0;
        });

        const packSelected = this.querySelector('[js-quantity-pack]:checked');
        const quantity = packSelected || this.quantityInput;
        const quantityValue = quantity ? parseInt(quantity.value) : 1;
        price = price * quantityValue;
        // Add-ons are added to the cart at the main product's quantity
        // (see product-form.js), so the displayed total multiplies them too.
        addOnTotal = addOnTotal * quantityValue;

        // Multi-pack pricing: "Set of 2/3/4" selections get 10% off the product
        // AND the add-ons (the automatic discount applies to those lines too).
        // Radios flagged with data-no-set-discount (e.g. curtains) are excluded.
        let compareTotal = null;
        if (packSelected && quantityValue >= 2 && !packSelected.hasAttribute('data-no-set-discount')) {
          compareTotal = Math.round((price + addOnTotal) * 100) / 100;
          price = price * 0.9;
          addOnTotal = addOnTotal * 0.9;
        }

        const total = Math.round((price + addOnTotal) * 100) / 100;
        sumEl.innerHTML =
          compareTotal !== null && compareTotal > total
            ? `${formatMoney(total)} <s class="nb-sum-compare">${formatMoney(compareTotal)}</s>`
            : formatMoney(total);

        const savingsEl = this.querySelector(`#Set-Savings-${this.dataset.section}`);
        if (savingsEl) {
          const savings = compareTotal !== null ? Math.round((compareTotal - total) * 100) / 100 : 0;
          const amountEl = savingsEl.querySelector('[js-set-savings-amount]');
          if (savings > 0 && amountEl) {
            amountEl.textContent = formatMoney(savings);
            savingsEl.removeAttribute('hidden');
          } else {
            savingsEl.setAttribute('hidden', '');
          }
        }

        if (this.dataset.sum === 'true' || packSelected) {
          this.updatePrice(sumEl);
        }
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initializeAddonListeners();

        // For combined listings, build the initial product combination map
        if (this.dataset.isCombinedListing === 'true') {
          this.loadCombinedListingData();
          this.buildProductCombinationMap();
        }

        // Initialize body.dataset.variant on page load for personalization images
        const variant = this.getSelectedVariant(this);
        if (variant?.id) {
          document.body.dataset.variant = variant.id;
        }

        // Check pack quantity inventory on initial load (delay to ensure product-form is ready)
        requestAnimationFrame(() => {
          this.checkPackQuantityInventory();
        });

        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: false }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          publish(PUB_SUB_EVENTS.sectionRefreshed, {
            data: {
              sectionId: this.sectionId,
              resource: {
                type: SECTION_REFRESH_RESOURCE_TYPE.product,
                id: newNode.dataset.productId,
              },
            },
          });
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        // Cancel any pending requests before starting a new one
        if (this.pendingRequestUrl) {
          this.abortController?.abort();
          this.pendingRequestUrl = null;
        }

        this._lastChangedOptionName = target.closest('fieldset')?.getAttribute('js-option') || null;

        this.logVariant('option changed by user', {
          option: this._lastChangedOptionName,
          value: target.value,
          selectedOptionValues,
          checked: this.getCheckedOptionsSummary(),
        });

        this.resetProductFormState();

        // For combined listings, we need to find the correct product URL based on ALL selected options
        let productUrl;
        if (this.dataset.isCombinedListing === 'true') {
          // Build/update the product combination map
          this.buildProductCombinationMap();

          // Try to find the product URL from the combination map using currently selected option TEXT values
          // Get all currently checked inputs and extract their values (text like "Hop", "Pink Trellis")
          const checkedInputs = this.variantSelectors?.querySelectorAll(
            'input:checked[js-variant-option], input:checked[js-variant-option-color]'
          );
          const selectedOptionTexts = Array.from(checkedInputs || [])
            .map((input) => input.value)
            .filter((v) => v)
            .sort();

          const lookupKey = selectedOptionTexts.join('|');

          if (this.productCombinationMap && this.productCombinationMap.has(lookupKey)) {
            const match = this.productCombinationMap.get(lookupKey);
            productUrl = match.productUrl;
          }

          // If not found in map, try the original logic
          if (!productUrl) {
            // Get all variant option inputs
            const allInputs = this.variantSelectors?.querySelectorAll(
              'input[js-variant-option], input[js-variant-option-color]'
            );

            // Check each input's variant data to see if it matches ALL selected option values
            if (allInputs) {
              for (const input of allInputs) {
                // Get the variant data for this input
                const variantScript = this.variantSelectors?.querySelector(`script[data-resource="${input.id}"]`);
                if (variantScript) {
                  try {
                    const variantData = JSON.parse(variantScript.textContent);
                    if (!variantData) continue;

                    // Check if ALL selected option values can be found in the variant data for this input
                    const productUrlForThisInput = input.dataset.productUrl;
                    if (productUrlForThisInput) {
                      // Get all inputs that share the same variant ID (they're the same variant, just different input types)
                      const variantId = variantData.id;
                      const allInputsForThisVariant = Array.from(allInputs).filter((i) => {
                        const script = this.variantSelectors?.querySelector(`script[data-resource="${i.id}"]`);
                        if (script) {
                          try {
                            const data = JSON.parse(script.textContent);
                            return data && data.id === variantId;
                          } catch (e) {
                            return false;
                          }
                        }
                        return false;
                      });

                      const optionValueIdsForThisVariant = allInputsForThisVariant.map((i) => i.dataset.optionValueId);

                      // Check if all selected option values match this variant's option values
                      const allSelectedValuesMatch =
                        selectedOptionValues.length === optionValueIdsForThisVariant.length &&
                        selectedOptionValues.every((id) => optionValueIdsForThisVariant.includes(id));

                      if (allSelectedValuesMatch) {
                        productUrl = productUrlForThisInput;
                        break;
                      }
                    }
                  } catch (e) {
                    console.error('Error parsing variant data:', e);
                  }
                }
              }
            }

            // Fallback if we couldn't determine the URL from searching inputs
            if (!productUrl) {
              productUrl = target.dataset.productUrl || this.dataset.url;
            }
          }
        } else {
          productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        }

        const shouldSwapProduct = this.dataset.url !== productUrl;
        // For combined listings, always use section rendering (faster!)
        // Only fetch full page for non-featured, non-combined-listing product swaps
        const shouldFetchFullPage =
          !this.isFeaturedProduct && shouldSwapProduct && this.dataset.isCombinedListing !== 'true';

        // Create new abort controller for this request
        this.abortController = new AbortController();

        const requestUrl = this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);
        this.logVariant('fetching section', { url: requestUrl, swapProduct: shouldSwapProduct });

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: shouldSwapProduct ? this.handleSwapProduct(productUrl) : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      // Opt-in console logging for the variant picker. Enable with
      //   window.NB_VARIANT_DEBUG = true                 (current page only)
      //   localStorage.setItem('nb-variant-debug', '1')  (persists across reloads)
      get variantDebugEnabled() {
        if (window.NB_VARIANT_DEBUG === true) return true;
        try {
          return window.localStorage?.getItem('nb-variant-debug') === '1';
        } catch (e) {
          return false;
        }
      }

      logVariant(message, details) {
        if (!this.variantDebugEnabled) return;
        if (details === undefined) {
          console.log(`[variant] ${message}`);
        } else {
          console.log(`[variant] ${message}`, details);
        }
      }

      // Returns { Embroidery: 'Bear Cub', Color: 'Sky', ... } for the visible fieldsets.
      getCheckedOptionsSummary(root = this.variantSelectors) {
        const summary = {};
        if (!root) return summary;
        root.querySelectorAll('fieldset[js-option]').forEach((fieldset) => {
          if (fieldset.style.display === 'none') return;
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          summary[fieldset.getAttribute('js-option')] = checked ? checked.value : null;
        });
        return summary;
      }

      get isFeaturedProduct() {
        return this.dataset.section.includes('featured_product');
      }

      handleSwapProduct(productUrl) {
        return (html) => {
          this.productModal?.remove();

          // Grab the selected variant from the new product info
          const productInfoNode = html.querySelector(`product-info[data-section=${this.sectionId}]`);
          const variant = this.getSelectedVariant(productInfoNode);

          this.updateURL(productUrl, variant?.id);

          // Declare combinedVariantTitle at function scope for use in setTimeout later
          let combinedVariantTitle = '';

          // For combined listings, look up the variant title and ID from the productCombinationMap
          // instead of using the child product's variant title
          if (this.dataset.isCombinedListing === 'true') {
            // Get currently selected option values
            const checkedInputs = this.variantSelectors?.querySelectorAll(
              'input:checked[js-variant-option], input:checked[js-variant-option-color]'
            );
            const selectedOptions = Array.from(checkedInputs || [])
              .map((input) => input.value)
              .filter((v) => v)
              .sort();

            // Look up the variant title and data from our preloaded map
            const lookupKey = selectedOptions.join('|');

            if (this.productCombinationMap && this.productCombinationMap.has(lookupKey)) {
              const match = this.productCombinationMap.get(lookupKey);
              combinedVariantTitle = match.variantTitle;
              // Use the variantId from the map (from loadCombinedListingData), or variantData.id (from buildProductCombinationMap), or fall back to variant.id
              const variantIdToUse = match.variantId || match.variantData?.id || variant?.id;
              document.body.dataset.variant = variantIdToUse;
            } else {
              // Fallback: construct from selected options
              combinedVariantTitle = selectedOptions.join(' / ');
              document.body.dataset.variant = variant?.id;
            }

            this.dataset.option1 = selectedOptions[0] || variant?.option1;
            this.dataset.option2 = selectedOptions[1] || variant?.option2;
            this.dataset.option3 = selectedOptions[2] || variant?.option3;
            this.dataset.variant = combinedVariantTitle;
          } else {
            // Regular product: use the variant's own title
            this.dataset.option1 = variant?.option1;
            this.dataset.option2 = variant?.option2;
            this.dataset.variant = variant?.title;
            document.body.dataset.variant = variant?.id;
          }

          // If we are in an embedded context (quick add, featured product, etc), only swap product info.
          // Otherwise, for combined listings, just update the form and info (media is preloaded).
          if (this.dataset.isCombinedListing === 'true') {
            // For combined listings, media is already preloaded - just toggle CSS visibility
            // No need to swap media gallery at all!

            // For combined listings, DON'T replace variant-selects (it has all combinations)
            // Only update the product form submit button and variant input
            const currentForm = this.querySelector('product-form');
            const newForm = html.querySelector('product-form');
            if (currentForm && newForm) {
              // Update the variant input value
              const currentVariantInput = currentForm.querySelector('input[name="id"]');
              const newVariantInput = newForm.querySelector('input[name="id"]');
              if (currentVariantInput && newVariantInput) {
                currentVariantInput.value = newVariantInput.value;
              }

              // Update the submit button state
              const currentSubmitButton = currentForm.querySelector('[name="add"]');
              const newSubmitButton = newForm.querySelector('[name="add"]');
              if (currentSubmitButton && newSubmitButton) {
                // Copy disabled state
                if (newSubmitButton.hasAttribute('disabled')) {
                  currentSubmitButton.setAttribute('disabled', 'disabled');
                } else {
                  currentSubmitButton.removeAttribute('disabled');
                }
                // Copy button text from server-rendered HTML (first span has the action text)
                const currentButtonText = currentSubmitButton.querySelector('span');
                const newButtonText = newSubmitButton.querySelector('span');
                if (currentButtonText && newButtonText) {
                  currentButtonText.textContent = newButtonText.textContent;
                }
              }
            }

            // Update variant-selects availability based on productCombinationMap
            // this.updateCombinedListingAvailability();

            // Update other product info sections - batch DOM updates
            const fetchedProductInfo = html.querySelector('product-info');
            const sourceSectionId = fetchedProductInfo?.dataset?.section || this.sectionId;

            // Batch all updates together
            const updates = [{ id: 'Estimated-Delivery' }, { id: 'price' }, { id: 'Description' }];

            updates.forEach(({ id }) => {
              const source = html.getElementById(`${id}-${sourceSectionId}`);
              const destination = this.querySelector(`#${id}-${this.dataset.section}`);
              if (source && destination) {
                destination.innerHTML = source.innerHTML;
              }
            });

            // Set the first visible image as active for the selected variant
            // Find the first visible media item that matches the current variant
            const mediaGallery = this.querySelector('media-gallery');
            if (mediaGallery) {
              // Use double requestAnimationFrame to ensure CSS has fully updated
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  // Query only within the gallery viewer (main image area), not thumbnails
                  const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
                  if (!viewer) return;

                  // Find all media items for this variant
                  const variantMediaItems = Array.from(
                    viewer.querySelectorAll('li[data-media-id][data-variant-title]')
                  ).filter((item) => item.dataset.variantTitle === combinedVariantTitle);

                  if (variantMediaItems.length > 0) {
                    const firstMediaId = variantMediaItems[0].dataset.mediaId;
                    const firstMediaElement = variantMediaItems[0];

                    // Set the active media (this updates the pagination dot)
                    mediaGallery.setActiveMedia?.(firstMediaId, false);

                    // Scroll the slider to the first image
                    const sliderList = viewer.querySelector('ul');
                    if (sliderList && firstMediaElement) {
                      sliderList.scrollTo({
                        left: firstMediaElement.offsetLeft,
                        behavior: 'smooth',
                      });
                    }
                  }
                });
              });
            }
          } else if (this.dataset.updateUrl === 'false') {
            // For other embedded contexts (quick add, featured product), swap product info
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            // Full page refresh for regular product swaps
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        // Store the current abort controller to ensure we're using the same one throughout this request
        const currentController = this.abortController;

        this.pendingRequestUrl = requestUrl;
        fetch(requestUrl, { signal: currentController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            // Only proceed if this is still the current request
            if (this.pendingRequestUrl !== requestUrl) {
              throw new Error('Request superseded');
            }

            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            // Use requestAnimationFrame for faster, smoother updates
            return new Promise((resolve) => requestAnimationFrame(() => resolve(html)));
          })
          .then((html) => {
            if (currentController.signal.aborted) {
              throw new Error('Request aborted');
            }
            callback(html);
            // Return immediately - no need to wait
            return Promise.resolve();
          })
          .then(() => {
            if (currentController.signal.aborted) {
              throw new Error('Request aborted');
            }
            // set focus to last clicked option value
            const targetElement = document.querySelector(`#${targetId}`);
            if (targetElement) {
              targetElement.focus();
            }
          })
          .catch((error) => {
            if (
              error.name === 'AbortError' ||
              error.message === 'Request superseded' ||
              error.message === 'Request aborted'
            ) {
              // Request was cancelled - this is expected behavior
              return;
            } else {
              console.error('Error in renderProductInfo:', error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) {
          return null;
        }
        const variantSelectsElement = productInfoNode.querySelector('variant-selects [data-selected-variant]');
        if (!variantSelectsElement || !variantSelectsElement.innerHTML) {
          return null;
        }
        try {
          const variant = JSON.parse(variantSelectsElement.innerHTML);
          return variant;
        } catch (e) {
          console.error('Error parsing variant JSON:', e);
          return null;
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (!variantSelects || !this.variantSelectors) return;

        // Filter out hidden fieldsets that are pending removal from previous viewTransition calls
        const currentFieldsets = Array.from(this.variantSelectors.querySelectorAll('fieldset')).filter(
          (fieldset) => fieldset.style.display !== 'none'
        );
        const newFieldsets = variantSelects.querySelectorAll('fieldset');

        const currentSelectedVariantScript = this.variantSelectors.querySelector('[data-selected-variant]');
        const newSelectedVariantScript = variantSelects.querySelector('[data-selected-variant]');

        if (variantSelects) {
          const lastChangedOptionName = this._lastChangedOptionName;
          this.logVariant('server returned options', {
            lastChanged: lastChangedOptionName,
            checked: this.getCheckedOptionsSummary(variantSelects),
          });
          currentFieldsets?.forEach((currentFieldset, index) => {
            const fieldsetName = currentFieldset.getAttribute('js-option');
            const isUserClickedFieldset = fieldsetName === lastChangedOptionName;
            const willPostProcess = index > 0 && !isUserClickedFieldset;
            const postProcessCallbacks = willPostProcess
              ? [(newFieldset) => this.autoSelectFirstAvailableOption(newFieldset)]
              : [];
            HTMLUpdateUtility.viewTransition(
              currentFieldset,
              newFieldsets[index],
              this.preProcessHtmlCallbacks,
              postProcessCallbacks
            );
          });

          HTMLUpdateUtility.viewTransition(
            currentSelectedVariantScript,
            newSelectedVariantScript,
            this.preProcessHtmlCallbacks
          );
        }
      }

      autoSelectFirstAvailableOption(fieldset) {
        const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
        if (!inputs.length) return;

        // Only intervene when the re-rendered fieldset has no checked input, i.e. the
        // previously-selected value was stripped by the hide-missing-variant filter.
        // If Shopify already returned a checked value, keep it: forcing the first
        // swatch here would override a valid selection (e.g. picking "Cover only"
        // would bounce Color from Sky back to Sage, and Wall Art sizes back to 18x12).
        const optionName = fieldset.getAttribute('js-option');
        const alreadyChecked = fieldset.querySelector('input[type="radio"]:checked');
        if (alreadyChecked) {
          this.logVariant(`keeping "${optionName}" = "${alreadyChecked.value}" (already selected)`);
          return;
        }

        const inStock = inputs.find((input) => !input.classList.contains('visually-disabled'));
        const target = inStock || inputs[0];
        target.checked = true;
        this.logVariant(`auto-selecting "${optionName}" = "${target.value}" (previous value unavailable)`, {
          variantId: target.dataset.variantId || null,
          candidates: inputs.map((input) => input.value),
        });

        // Shopify's option_values lookup falls back to first-available when the matched
        // variant is sold out, so fetch by variant id directly to ensure it actually selects.
        const variantId = target.dataset.variantId;
        if (variantId && this.dataset.isCombinedListing !== 'true') {
          this.fetchByVariantId(variantId, target, optionName);
        } else {
          target.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      fetchByVariantId(variantId, target, optionName) {
        if (this.pendingRequestUrl) {
          this.abortController?.abort();
          this.pendingRequestUrl = null;
        }
        this.resetProductFormState();
        this.abortController = new AbortController();
        this._lastChangedOptionName = optionName;

        const fieldset = target.closest('fieldset');
        const legendSpan = fieldset?.querySelector('[data-selected-value]');
        if (legendSpan) legendSpan.innerHTML = target.value;

        const productUrl = this.dataset.url;
        const requestUrl = `${productUrl}?section_id=${this.sectionId}&variant=${variantId}`;
        this.logVariant('fetching section by variant id', { url: requestUrl });

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: this.handleUpdateProductInfo(productUrl),
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          // If we're in quick-add context, transform all IDs in the response HTML
          if (this.dataset.originalSection) {
            const originalSection = this.dataset.originalSection;
            const quickAddSection = `quickadd-${originalSection}`;

            // Transform the entire HTML document
            html.documentElement.innerHTML = html.documentElement.innerHTML.replaceAll(
              originalSection,
              quickAddSection
            );
          }

          const variant = this.getSelectedVariant(html);

          this.logVariant(
            'selected variant',
            variant ? { id: variant.id, title: variant.title, available: variant.available } : null
          );

          // update selected variant
          this.dataset.option1 = variant?.option1;
          this.dataset.option2 = variant?.option2;
          this.dataset.option3 = variant?.option3;
          this.dataset.variant = variant?.title;

          document.body.dataset.variant = variant?.id;

          this.pickupAvailability?.update(variant);

          this.updateOptionValues(html);

          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);
          this.updateQuantityInput();
          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const sourceSectionId = this.dataset.originalSection ? this.dataset.section : this.sectionId;
            const source = html.getElementById(`${id}-${sourceSectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination && destination.tagName === 'INPUT') {
              // Inputs (e.g. the hidden properties[ETA] field) have no innerHTML —
              // copy the value and the standard/personalization ETA variants instead,
              // keeping the personalization ETA active if it was selected.
              const wasPersonalization =
                destination.dataset.personalizationEta &&
                destination.value === destination.dataset.personalizationEta;
              if (source.dataset.standardEta) destination.dataset.standardEta = source.dataset.standardEta;
              if (source.dataset.personalizationEta)
                destination.dataset.personalizationEta = source.dataset.personalizationEta;
              destination.value = wasPersonalization ? destination.dataset.personalizationEta : source.value;
            } else if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }

            if (id == 'Sum') {
              if (destination && source) {
                destination.dataset.price = source.dataset.price;
              }
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Estimated-Delivery');
          updateSourceFromDestination('Estimated-Delivery-Input');
          updateSourceFromDestination('Shipping-Method-Input');
          updateSourceFromDestination('Variant-Addons');
          updateSourceFromDestination('Wallpaper-Manual');
          updateSourceFromDestination('Trundle');
          // update sum
          updateSourceFromDestination('Sum');

          // update sum
          updateSourceFromDestination('Sample');

          // add the addons
          this.updateSum();

          // this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const buttonSectionId = this.dataset.originalSection ? this.dataset.section : this.sectionId;
          const serverSubmitButton = html.getElementById(`ProductSubmitButton-${buttonSectionId}`);
          this.productForm?.toggleSubmitButton(
            serverSubmitButton?.hasAttribute('disabled') ?? true,
            serverSubmitButton?.querySelector('span')?.textContent || window.variantStrings.soldOut
          );

          // Check pack quantity inventory after variant change
          this.checkPackQuantityInventory();

          this.productForm?.togglePersonalizeButton(
            html.getElementById(`ProductPopup-${buttonSectionId}`)?.hasAttribute('disabled') ?? true
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          // Reinitialize addon event listeners after updating the addons
          this.initializeAddonListeners();
        };
      }

      updateQuantityInput() {
        const quantityInput = this.querySelector('[js-quantity-input]');
        const packQuantityInputs = this.querySelectorAll('[js-quantity-pack]');

        if (!quantityInput) {
          return;
        }

        if (packQuantityInputs.length === 0) {
          return;
        }

        packQuantityInputs.forEach((input) => {
          input.addEventListener('change', (event) => {
            const value = parseInt(event.target.value);
            quantityInput.value = value;

            // Create a new 'change' event
            var event = new Event('change');

            // Dispatch it.
            quantityInput.dispatchEvent(event);

            // Check if pack quantity exceeds available inventory
            this.checkPackQuantityInventory();

            // Swap the gallery to the matching "Set of N" image, if one exists
            this.updatePackMedia(value);
          });
        });
      }

      updatePackMedia(packSize) {
        if (packSize >= 2) {
          this.dataset.pack = packSize;
        } else {
          delete this.dataset.pack;
        }

        const mediaGallery = this.querySelector('media-gallery');
        if (!mediaGallery) return;

        const variant = this.getSelectedVariant(this);

        let targetId;
        if (packSize >= 2) {
          // Prefer the set shot tagged for the selected variant — media alt text
          // holds the variant it belongs to (e.g. "Bluejay / Set of 2"). Fall back
          // to an untagged set shot (alt exactly "Set of N"); never activate a set
          // shot tagged for a different variant, since the gallery CSS only shows
          // set images matching the selected color.
          const candidates = Array.from(
            mediaGallery.querySelectorAll(`.product__media-item[data-alt*="Set of ${packSize}"]`)
          );
          const variantValues = [variant?.title]
            .concat(variant?.options || [])
            .filter(Boolean)
            .map((v) => String(v).toLowerCase());
          const variantMatch = candidates.find((item) => {
            const alt = (item.dataset.alt || '').toLowerCase();
            return variantValues.some((v) => alt.includes(v));
          });
          const untagged = candidates.find(
            (item) => (item.dataset.alt || '').trim().toLowerCase() === `set of ${packSize}`
          );
          targetId = (variantMatch || untagged)?.dataset.mediaId;
        }

        // No pack selected, or no set shot exists for this variant: show the
        // variant's featured media so the gallery never sits on a stale slide.
        if (!targetId) {
          if (variant?.featured_media?.id) {
            targetId = `${this.dataset.section}-${variant.featured_media.id}`;
          }
          if (!targetId || !mediaGallery.querySelector(`[data-media-id="${targetId}"]`)) {
            targetId = mediaGallery.querySelector('.product__media-item:not([data-alt*="Set of"])')?.dataset.mediaId;
          }
        }

        if (targetId) mediaGallery.setActiveMedia?.(targetId, true);
      }

      checkPackQuantityInventory() {
        const packQuantityInputs = this.querySelectorAll('[js-quantity-pack]');
        if (packQuantityInputs.length === 0) return;

        const selectedPack = this.querySelector('[js-quantity-pack]:checked');
        if (!selectedPack) return;

        const requestedQuantity = parseInt(selectedPack.value);
        const warningElement = this.querySelector(`#pack-quantity-warning-${this.dataset.section}`);
        const availableSpan = this.querySelector(`#pack-quantity-available-${this.dataset.section}`);

        if (!warningElement) return;

        // Get current variant inventory from custom inventory data
        const variantSelects = this.querySelector('variant-selects');
        let availableInventory = null;

        if (variantSelects) {
          const selectedVariantScript = variantSelects.querySelector('[data-selected-variant]');
          const inventoryDataScript = variantSelects.querySelector('[data-variant-inventory]');

          if (selectedVariantScript && selectedVariantScript.innerHTML && inventoryDataScript && inventoryDataScript.innerHTML) {
            try {
              const variant = JSON.parse(selectedVariantScript.innerHTML);
              const inventoryData = JSON.parse(inventoryDataScript.innerHTML);
              const variantInventory = inventoryData[variant.id];

              if (variantInventory && variantInventory.inventory_management === 'shopify') {
                availableInventory = variantInventory.inventory_quantity;
              }
            } catch (e) {
              console.error('Error parsing inventory data:', e);
            }
          }
        }

        // Check if requested quantity exceeds available inventory
        const hasInsufficientInventory =
          availableInventory !== null &&
          availableInventory > 0 &&
          requestedQuantity > availableInventory;

        if (hasInsufficientInventory) {
          // Show warning
          warningElement.removeAttribute('hidden');
          if (availableSpan) {
            availableSpan.textContent = availableInventory;
          }

          // Disable add to cart button
          if (this.productForm && typeof this.productForm.toggleSubmitButton === 'function') {
            this.productForm.toggleSubmitButton(true, window.variantStrings.soldOut);
          }
        } else {
          // Hide warning
          warningElement.setAttribute('hidden', '');

          // Re-enable add to cart button (if variant is available)
          if (variantSelects) {
            const selectedVariantScript = variantSelects.querySelector('[data-selected-variant]');
            if (selectedVariantScript && selectedVariantScript.innerHTML) {
              try {
                const variant = JSON.parse(selectedVariantScript.innerHTML);
                if (variant && variant.available) {
                  if (this.productForm && typeof this.productForm.toggleSubmitButton === 'function') {
                    this.productForm.toggleSubmitButton(false);
                  }
                }
              } catch (e) {
                console.error('Error parsing variant data:', e);
              }
            }
          }
        }
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              const newItem = mediaGalleryDestinationItems[i];

              mediaGallerySource.prepend(newItem);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        if (variantFeaturedMediaId) {
          const activePack = parseInt(this.dataset.pack, 10);
          if (activePack >= 2) {
            // A "Set of N" pack is selected: stay on the set shot for the newly
            // selected variant instead of jumping to its featured media.
            this.updatePackMedia(activePack);
          } else {
            // set featured media as active in the media gallery
            this.querySelector(`media-gallery`)?.setActiveMedia?.(
              `${this.dataset.section}-${variantFeaturedMediaId}`,
              true
            );
          }

          // update media modal
          const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
          const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
          if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }

        // Determine the value to set
        let valueToSet = min;

        // Check if pack quantity exists and is selected
        const selectedPack = this.querySelector('[js-quantity-pack]:checked');
        if (selectedPack) {
          // Use pack value, but ensure it's within bounds
          const packValue = parseInt(selectedPack.value);
          valueToSet = packValue;

          // If pack value exceeds max, reset to min and update the radio
          if (max && packValue > max) {
            valueToSet = min;
            const minPackInput = this.querySelector(`[js-quantity-pack][value="${min}"]`);
            if (minPackInput) {
              minPackInput.checked = true;
            }
          }
        } else {
          // No pack options, preserve current value if it's within bounds
          const currentValue = parseInt(this.quantityInput.value);
          if (currentValue >= min && (!max || currentValue <= max)) {
            valueToSet = currentValue;
          }
        }

        this.quantityInput.value = valueToSet;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }

        // this?.querySelector('[js-quantity-pack][value="1"]')?.click();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      showLoader() {
        // Create loader overlay if it doesn't exist
        if (!this.loader) {
          this.loader = document.createElement('div');
          this.loader.className = 'product-info-loader';
          this.loader.innerHTML =
            '<div class="loading__spinner"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>';

          // Add styles if not already present
          if (!document.getElementById('product-info-loader-styles')) {
            const style = document.createElement('style');
            style.id = 'product-info-loader-styles';
            style.textContent = `
              .product-info-loader {
                position: fixed;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background: rgba(255, 255, 255, 0.8);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 9999;
                opacity: 0;
                transition: opacity 0.2s ease;
              }
              .product-info-loader.visible {
                opacity: 1;
              }
            `;
            document.head.appendChild(style);
          }
        }

        document.body.appendChild(this.loader);
        // Trigger reflow to enable transition
        this.loader.offsetHeight;
        this.loader.classList.add('visible');
      }

      hideLoader() {
        if (this.loader) {
          this.loader.classList.remove('visible');
          setTimeout(() => {
            this.loader?.remove();
          }, 200);
        }
      }

      loadCombinedListingData() {
        // Load the combined listing data from the Liquid-generated script tag
        const dataScript = document.querySelector(`script[data-combined-listing-map="${this.dataset.section}"]`);
        if (!dataScript) return;

        try {
          const data = JSON.parse(dataScript.textContent);

          // Pre-populate the map with ALL combinations from the parent product
          if (!this.productCombinationMap) {
            this.productCombinationMap = new Map();
          }

          data.combinations.forEach((combo) => {
            const optionValues = combo.options.filter((v) => v && v !== 'Default Title');
            if (optionValues.length > 0) {
              const key = optionValues.sort().join('|');
              this.productCombinationMap.set(key, {
                productUrl: combo.productUrl,
                variantTitle: combo.variantTitle,
                variantId: combo.variantId,
                optionValues,
                available: combo.available,
              });
            }
          });
        } catch (e) {
          console.error('Error loading combined listing data:', e);
        }
      }

      buildProductCombinationMap() {
        if (!this.variantSelectors) return;

        // Build a map of option text combinations to product URLs
        // This persists across product swaps and uses the actual option values (e.g., "Hop", "Pink Trellis")
        if (!this.productCombinationMap) {
          this.productCombinationMap = new Map();
        }

        const allInputs = this.variantSelectors.querySelectorAll(
          'input[js-variant-option], input[js-variant-option-color]'
        );

        allInputs.forEach((input) => {
          const productUrl = input.dataset.productUrl;
          const variantScript = this.variantSelectors.querySelector(`script[data-resource="${input.id}"]`);

          if (productUrl && variantScript) {
            try {
              const variantData = JSON.parse(variantScript.textContent);

              // Skip null variant data (happens on child products where some combinations don't exist)
              if (!variantData) return;

              // Use the option text values from the variant (e.g., "Hop", "Blue Botanical")
              // These are in variantData.option1, option2, option3
              const optionValues = [variantData.option1, variantData.option2, variantData.option3].filter(
                (v) => v && v !== 'Default Title'
              ); // Filter out null/undefined and Default Title

              if (optionValues.length > 0) {
                const key = optionValues.sort().join('|');
                if (!this.productCombinationMap.has(key)) {
                  this.productCombinationMap.set(key, {
                    productUrl,
                    variantData,
                    optionValues,
                    available: variantData.available,
                  });
                }
              }
            } catch (e) {
              console.error('Error building product combination map:', e);
            }
          }
        });
      }

      updateCombinedListingAvailability() {
        if (!this.variantSelectors || !this.productCombinationMap) return;

        // Get all fieldsets
        const fieldsets = Array.from(this.variantSelectors.querySelectorAll('fieldset'));

        fieldsets.forEach((currentFieldset, currentFieldsetIndex) => {
          const inputs = currentFieldset.querySelectorAll('input[js-variant-option], input[js-variant-option-color]');

          inputs.forEach((input) => {
            // Build test combination: get selected value from each fieldset,
            // but use this input's value for the current fieldset
            const testOptions = fieldsets
              .map((fieldset, fieldsetIndex) => {
                // If this is the current fieldset, use the input's value we're testing
                if (fieldsetIndex === currentFieldsetIndex) {
                  return input.value;
                }
                // Otherwise, get the checked input's value from that fieldset
                const checkedInput = fieldset.querySelector(
                  'input:checked[js-variant-option], input:checked[js-variant-option-color]'
                );
                return checkedInput ? checkedInput.value : null;
              })
              .filter((v) => v); // Remove null values

            const testKey = testOptions.sort().join('|');

            // Check if this combination exists AND is available
            const combination = this.productCombinationMap.get(testKey);
            const isAvailable = combination && combination.available;

            // Update disabled state for all inputs (including checked ones)
            if (isAvailable) {
              input.classList.remove('disabled');
              input.classList.remove('visually-disabled');
            } else {
              input.classList.add('disabled');
              input.classList.add('visually-disabled');
            }
          });
        });
      }

      // Add this new method to handle addon initialization
      initializeAddonListeners() {
        const addons = this.querySelectorAll('[js-addon]');

        for (const addon of addons) {
          const inputs = addon.querySelectorAll('[js-addon-option]');

          for (const input of inputs) {
            input.addEventListener('change', () => {
              this.updateSum();

              const addonText = addon.querySelector('[js-addon-selected-option]');

              let image =
                input.getAttribute('data-image') === null ||
                input.getAttribute('data-image') === '' ||
                input.getAttribute('data-image') === undefined
                  ? ''
                  : `<span class='nb-addon-selected-option-image'><img src="${input.getAttribute('data-image')}" alt="${
                      input.value
                    }" /></span>`;

              const priceDisplay = input.getAttribute('data-price-display');
              const price = priceDisplay
                ? `<span class='nb-addon-selected-option-price'>${priceDisplay}</span>`
                : '';

              if (addonText) {
                addonText.innerHTML = `${image}
                  <span>
                    ${input.value}
                  </span>
                  ${price}`;
              }

              addon.removeAttribute('open');
            });
          }
        }
      }
    }
  );
}

